import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { ContextActionType, useDumbActionsContext } from '@europrocurement/flexy-components';

import {
    ColumnDatatable,
    FilterDatatable,
    FilterOptionValue,
    FiltersDatatableList,
} from '@europrocurement/flexy-datatable';

import { PackageJsonldPackageRead } from '@europrocurement/l2d-domain/openApi/ApiOffre';
import { addItem, FaOptionIcon } from '@europrocurement/l2d-icons';
import { CatalogsAdmin, useOffreService } from '@europrocurement/l2d-domain';
import { Typography } from '@mui/material';
import { Box } from '@mui/system';
import RoutedListing from '../components/RoutedListing';

import { getStringColumn } from '../listings';
import useFilterOptions from './useFilterOptions';
import usePackageSearch from './usePackageSearch';
import { PossibleTypes } from '../../Societes';
import { catalogCreatePath, catalogFichePath } from './routes';
import { offerFichePath } from '../Offer';
import LinkToColumn from './Component/LinkToColumn';
import { groupFichePath } from '../Group';
import { packageFichePath } from '../Package';
import EllipsisCell from '../components/EllipsisCell';

const Panel: React.FunctionComponent = function () {
    const navigate = useNavigate();
    const { catalogModel: model } = useOffreService();
    const filterOptions = useFilterOptions();
    const { searchPackage } = usePackageSearch();

    const goToCreate = React.useCallback(() => navigate(catalogCreatePath), [navigate]);
    const goToFiche = React.useCallback(
        (item: { id: string | number }) => navigate(catalogFichePath(item.id)),
        [navigate],
    );

    const columns: ColumnDatatable<CatalogsAdmin>[] = [
        {
            ...getStringColumn('nomCommercial', undefined),
            sortable: true,
        },
        {
            label: 'Nom du package associé',
            displayLabel: true,
            render: (item) => {
                const cataloguePackageFormality = item?.package as
                    | { nomCommercial?: string; id?: number }
                    | undefined;
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: '0.2em' }}>
                        <EllipsisCell
                            text={cataloguePackageFormality?.nomCommercial ?? '(aucun package)'}
                        />
                        {cataloguePackageFormality?.id ? (
                            <LinkToColumn
                                link={packageFichePath(cataloguePackageFormality?.id as number)}
                                tooltip="Ouvrir l'offre"
                            />
                        ) : null}
                    </Box>
                );
            },
            isDisplayed: true,
            sortable: false,
            field: 'package',
        },
        {
            label: 'Periode',
            displayLabel: true,
            isDisplayed: true,
            sortable: false,
            render: (item) => {
                const startDate = item.dateDebut;
                const endDate = item.dateFin;
                const toFr = (value?: string | null): string | null => {
                    if (!value) {
                        return null;
                    }
                    const date = new Date(value);
                    return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString('fr-FR');
                };

                const startFr = toFr(startDate);
                const endFr = toFr(endDate);

                if (!startFr && !endFr) {
                    return undefined;
                }

                return (
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                    >
                        {`${startFr ?? '…'} → ${endFr ?? '…'}`}
                    </Typography>
                );
            },
        },
        {
            label: 'Offre',
            displayLabel: true,
            render: (item) => (
                <Typography
                    variant="l2dtableCell"
                    color="text.gris70"
                >
                    {item.offre?.libelle ?? '(aucun groupement)'}
                    {(item.offre as { id?: number } | undefined)?.id && (
                        <LinkToColumn
                            link={offerFichePath((item.offre as { id?: number }).id as number)}
                            tooltip="Ouvrir l'offre"
                        />
                    )}
                </Typography>
            ),
            isDisplayed: true,
            field: 'offre',
            sortable: true,
        },
        {
            label: 'Groupement',
            displayLabel: true,
            render: (item) => (
                <Typography
                    variant="l2dtableCell"
                    color="text.gris70"
                >
                    {item.groupement?.libelle ?? '(aucun groupement)'}
                    {(item.groupement as { id?: number } | undefined)?.id && (
                        <LinkToColumn
                            link={groupFichePath((item.groupement as { id?: number }).id as number)}
                            tooltip="Ouvrir le groupement"
                        />
                    )}
                </Typography>
            ),
            isDisplayed: true,
            field: 'groupement',
            sortable: true,
        },
        {
            label: 'Domaine / Origine',
            displayLabel: true,
            render: (item) => (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.2em' }}>
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                    >
                        {item.domaine?.libelle ?? '(aucun domaine)'}
                    </Typography>

                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                    >
                        {item.origine?.origine ?? '(aucune origine)'}
                    </Typography>
                </Box>
            ),
            isDisplayed: true,
            sortable: false,
            field: 'domaine',
        },
    ];

    const contextActions: ContextActionType[] = React.useMemo(
        () => [
            {
                name: 'Créer un nouveau catalogue',
                icon: <FaOptionIcon icon={['fas', 'plus-circle']} />,
                action: goToCreate,
            },
        ],
        [goToCreate],
    );

    useDumbActionsContext(contextActions);

    /** Generic function : Generate filter object */
    const getFilter = (
        label: string,
        field: string,
        options: FilterOptionValue[],
    ): FilterDatatable => ({
        label,
        field,
        type: 'staticSelect' as const,
        options,
        renderFilterList: (value: string) => {
            const selectedOption = options.find((option) => option.value === value);
            return selectedOption ? selectedOption.label : 'Aucun';
        },
    });

    const filters: FiltersDatatableList = [
        getFilter('Domaine', 'domaineId', filterOptions.domains),
        getFilter('Offre', 'offreId', filterOptions.offers),
        {
            label: 'Package',
            field: 'packageId',
            type: 'selectApi',
            searchFunction: searchPackage,
            renderItem: (item: PackageJsonldPackageRead) =>
                (item.nomCommercial || item.libelle) as string,
            valueMap: (item: PackageJsonldPackageRead) => item.id as unknown as string,
        },
        getFilter('Groupement', 'groupementId', filterOptions.groups),
        getFilter('Origine', 'origineId', filterOptions.origins),
        {
            label: 'Début à partir du',
            field: 'dateDebutAfter',
            type: 'date',
        },
        {
            label: 'Début jusqu’au',
            field: 'dateDebutBefore',
            type: 'date',
        },
        {
            label: 'Fin à partir du',
            field: 'dateFinAfter',
            type: 'date',
        },
        {
            label: 'Fin jusqu’au',
            field: 'dateFinBefore',
            type: 'date',
        },
    ];

    return (
        <RoutedListing
            listingTitle="Liste des catalogues"
            createButton={{
                label: 'Créer un nouveau catalogue',
                icon: addItem,
                onClick: goToCreate,
            }}
            listingColumns={columns as ColumnDatatable<unknown>[]}
            model={model}
            filters={filters}
            filtersControl
            searchBarType={PossibleTypes.Catalogue}
            onRowClick={goToFiche}
        />
    );
};

export default Panel;
